import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { Box } from 'rebass/styled-components';
import { Loader, MessageBlock } from '@getro/rombo';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import NetworkSchema from '../../../schemas/network';
import { AdminList } from '../../molecules/adminList';
import strings from '../../../strings';
import { InviteAdminModal } from './inviteAdminModal';
import RemoveAdminModal from './removeAdminModal';
import UserSchema from '../../../schemas/user';

export const AdminPanel = ({
  onAdminInvite,
  onRemoveAdmin,
  onResendInvitation,
  adminGroupName,
  admins,
  location,
  activeNetwork,
  currentUser,
  canAddCompanyAdmins,
  canRemoveCompanyAdmins,
  fetchError,
  isLoading,
  isInvitingAdmin,
  isResendingInvitation,
  isDeletingAdmin,
  title,
  showEmailAddress,
  companyAdmins,
  invitedUsers,
}) => {
  const formRef = useRef(null);
  const { action } = queryString.parse(location.search);
  // Check both actions to support old 'invite-manager'
  const inviteModalOpen = action === 'invite-admin' || action === 'invite-manager';
  const [isInviteAdminModalOpen, setIsInviteAdminModalOpen] = useState(inviteModalOpen);
  const [inviteAdminError, setInviteAdminError] = useState('');
  const [removingAdmin, setRemovingAdmin] = useState(null);
  const history = useHistory();

  useEffect(() => {
    if (removingAdmin !== null) {
      const removingAdminFound = admins.find(({ id }) => id === removingAdmin.id);
      if (!removingAdminFound) {
        setRemovingAdmin(null);
      }
    }
  }, [removingAdmin, admins]);

  const onRemoveAdminRequest = (user) => {
    setRemovingAdmin(user);
  };

  return (
    <Box
      data-testid="admin-panel"
      mb="40px"
      title={title}
      headerButton={{
        children: 'Invite',
        display: canAddCompanyAdmins && !fetchError && admins && admins.length ? 'flex' : 'none',
        onClick: () => {
          setIsInviteAdminModalOpen(true);
        },
        variant: 'secondary',
        alignItems: 'center',
        width: 'max-content',
        fontSize: 1,
        lineHeight: '14px',
        px: 4,
        py: 2,
      }}
    >
      {isLoading && <Loader data-testid="loader" />}
      {!isLoading && (
        <>
          {fetchError && <MessageBlock data-testid="fetch-error">{strings.genericError}</MessageBlock>}
          {!fetchError && (
            <>
              <AdminList
                users={admins}
                invitedUsers={invitedUsers}
                onRemoveAdmin={canRemoveCompanyAdmins && onRemoveAdmin ? onRemoveAdminRequest : null}
                onResendInvitation={canAddCompanyAdmins ? onResendInvitation : null}
                isResendingInvitation={isResendingInvitation}
                onEmptyListClick={() => {
                  setIsInviteAdminModalOpen(true);
                }}
                showEmailAddress={showEmailAddress}
              />
              <InviteAdminModal
                isSubmitting={isInvitingAdmin}
                onAdminInvite={(values) => {
                  history.replace({ search: '' });
                  onAdminInvite(values)
                    .then(() => {
                      setIsInviteAdminModalOpen(false);
                      toast('Invitation sent', {
                        icon: 'success',
                        id: 'invite-admin',
                      });
                    })
                    .catch((error) => {
                      const errorCode = error?.data?.code;
                      let errorMessage = error?.data?.error;

                      if (errorCode === 'user_invitation.user_needs_company_email') {
                        errorMessage = `For security reasons, only users with an email address matching the company domain can be
                            added as admins.
                            <br />
                            <br />
                            If another domain should be allowed, <a href="mailto:support@getro.com">contact us</a>`;
                      }

                      setInviteAdminError(errorMessage);
                    });
                }}
                companyName={adminGroupName}
                modalOpen={isInviteAdminModalOpen}
                setModalOpen={setIsInviteAdminModalOpen}
                formRef={formRef}
                companyAdmin={companyAdmins}
                apiError={inviteAdminError}
                setApiError={setInviteAdminError}
              />
              {canRemoveCompanyAdmins && onRemoveAdmin && (
                <RemoveAdminModal
                  user={removingAdmin}
                  currentUser={currentUser}
                  network={activeNetwork}
                  isSubmitting={isDeletingAdmin}
                  modalOpen={removingAdmin !== null}
                  onCancel={() => setRemovingAdmin(null)}
                  onDeleteAdmin={() => onRemoveAdmin(removingAdmin)}
                />
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

AdminPanel.propTypes = {
  isInvitingAdmin: PropTypes.bool,
  isDeletingAdmin: PropTypes.bool,
  isResendingInvitation: PropTypes.bool,
  adminGroupName: PropTypes.string.isRequired,
  activeNetwork: NetworkSchema.isRequired,
  currentUser: UserSchema.isRequired,
  onAdminInvite: PropTypes.func.isRequired,
  admins: PropTypes.array,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  onRemoveAdmin: PropTypes.func,
  onResendInvitation: PropTypes.func,
  canAddCompanyAdmins: PropTypes.bool,
  canRemoveCompanyAdmins: PropTypes.bool,
  fetchError: PropTypes.bool,
  isLoading: PropTypes.bool,
  title: PropTypes.string,
  showEmailAddress: PropTypes.bool,
  companyAdmins: PropTypes.bool,
  invitedUsers: PropTypes.array,
};

AdminPanel.defaultProps = {
  isInvitingAdmin: false,
  isDeletingAdmin: false,
  isResendingInvitation: false,
  admins: [],
  location: {},
  onRemoveAdmin: () => {},
  onResendInvitation: () => {},
  canAddCompanyAdmins: false,
  canRemoveCompanyAdmins: false,
  fetchError: false,
  isLoading: false,
  title: 'Company admins',
  showEmailAddress: false,
  companyAdmins: true,
  invitedUsers: [],
};
